import React, { useMemo } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, paperClasses, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TrustedSupplierIcon from '../assets/icons/trusted_icon.svg?react';
import TrustedSupplierProposition from '../assets/images/trusted_supplier_proposition.png';
import TrustedSupplierValidation from '../assets/images/trusted_supplier_validation.png';
import { ContractPreview } from '../hooks/useContracts';
import useGetValidatedOrdersOnFirstTryCount from './useGetValidatedOrdersOnFirstTryCount';

interface TrustedPartnerDialogProps {
  open: boolean;
  onClose: (activated?: boolean) => void;
  contract: ContractPreview;
}

export default function TrustedPartnerDialog({ open, onClose, contract }: TrustedPartnerDialogProps): React.JSX.Element {
  const { t } = useTranslation(['contracts', 'common']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleActivation = (): void => {
    onClose(true);
  };

  const handleClose = (): void => {
    onClose(false);
  };
  const validatedOnFirstTryCount = useGetValidatedOrdersOnFirstTryCount({
    buyerCompanyId: contract.buyingCompanyId,
    supplierCompanyId: contract.supplyingCompanyId,
  });

  const propositionTitle = useMemo(() => {
    return (
      <Typography variant="titleMedium">
        {t('contracts:trusted_supplier.proposition_title', { count: validatedOnFirstTryCount })}
      </Typography>
    );
  }, [validatedOnFirstTryCount]);

  const validationTitle = useMemo(() => {
    return (
      <Typography variant="titleMedium">
        {t('contracts:trusted_supplier.validation_title', { companyName: contract.company.tradeName })}
      </Typography>
    );
  }, [contract]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      sx={{
        [`& .${paperClasses.root}`]: {
          width: 580,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexDirection: 'row',
        }}
      >
        <TrustedSupplierIcon width={24} height={24} />
        {t('contracts:trusted_supplier.dialog_title')}
      </DialogTitle>
      <DialogContent sx={{ mt: 0.5, pb: 0 }}>
        {!!validatedOnFirstTryCount && (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1.5, sm: 2 } }}>
                <Box
                  component="img"
                  src={TrustedSupplierProposition}
                  sx={{
                    width: {
                      xs: 40,
                      sm: 72,
                    },
                    height: {
                      xs: 53,
                      sm: 96,
                    },
                  }}
                />
                {isMobile && propositionTitle}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {!isMobile && propositionTitle}
                <Typography
                  variant="bodyMedium"
                  sx={{
                    color: 'onSurfaceVariant.main',
                  }}
                >
                  {t('contracts:trusted_supplier.proposition_description')}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1.5, sm: 2 } }}>
            <Box
              component="img"
              src={TrustedSupplierValidation}
              sx={{
                width: {
                  xs: 40,
                  sm: 72,
                },
                height: {
                  xs: 40,
                  sm: 72,
                },
              }}
            />
            {isMobile && validationTitle}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!isMobile && validationTitle}
            <Typography
              variant="bodyMedium"
              sx={{
                color: 'onSurfaceVariant.main',
              }}
            >
              {t('contracts:trusted_supplier.validation_description')}
            </Typography>
            <Typography variant="bodyMedium" sx={{ mt: 2 }}>
              {t('contracts:trusted_supplier.confirmation_text')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
        <Button
          sx={{
            width: {
              xs: '100%',
              sm: 'auto',
            },
          }}
          variant="outlined"
          onClick={handleClose}
        >
          {t('common:maybe_later')}
        </Button>
        <Button
          sx={{
            width: {
              xs: '100%',
              sm: 'auto',
            },
          }}
          variant="contained"
          onClick={handleActivation}
        >
          {t('contracts:trusted_supplier.activate_partnership')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
